import React, { useMemo } from "react";
import { Box, useMediaQuery } from "@mui/material";
import {
  CheckCircleOutline as ConfirmIcon,
  PlayArrowRounded as StartIcon,
  BuildCircleOutlined as ProgressIcon,
  HourglassBottomRounded as WaitingIcon,
  ViewListRounded as AllIcon,
} from "@mui/icons-material";
import { OfferStatusCode, OffersFilters } from "../models/models";
import { columnThemes } from "./ActiveWorkCard";
import { dt } from "../../../shared/themes/redesignTokens";

// ════════════════════════════════════════════════════════════════
//  Strip badge config
// ════════════════════════════════════════════════════════════════

interface StripBadge {
  statusCode: OfferStatusCode;
  labelKey: string;
  icon: React.ReactElement;
  accent: string;
  bg: string;
  glow: string;
  pulse?: boolean;
}

const STRIP_BADGES: StripBadge[] = [
  {
    statusCode: OfferStatusCode.awaitingConfirmation,
    labelKey: "actionStrip.needConfirmation",
    icon: <ConfirmIcon sx={{ fontSize: "15px" }} />,
    accent: columnThemes[OfferStatusCode.awaitingConfirmation].accent,
    bg: columnThemes[OfferStatusCode.awaitingConfirmation].bg,
    glow: columnThemes[OfferStatusCode.awaitingConfirmation].glow,
    pulse: true,
  },
  {
    statusCode: OfferStatusCode.confirmed,
    labelKey: "actionStrip.confirmedToday",
    icon: <StartIcon sx={{ fontSize: "15px" }} />,
    accent: columnThemes[OfferStatusCode.confirmed].accent,
    bg: columnThemes[OfferStatusCode.confirmed].bg,
    glow: columnThemes[OfferStatusCode.confirmed].glow,
  },
  {
    statusCode: OfferStatusCode.workInProgress,
    labelKey: "actionStrip.inProgress",
    icon: <ProgressIcon sx={{ fontSize: "15px" }} />,
    accent: columnThemes[OfferStatusCode.workInProgress].accent,
    bg: columnThemes[OfferStatusCode.workInProgress].bg,
    glow: columnThemes[OfferStatusCode.workInProgress].glow,
  },
  {
    statusCode: OfferStatusCode.workCompleted,
    labelKey: "actionStrip.awaitingCustomer",
    icon: <WaitingIcon sx={{ fontSize: "15px" }} />,
    accent: columnThemes[OfferStatusCode.workCompleted].accent,
    bg: columnThemes[OfferStatusCode.workCompleted].bg,
    glow: columnThemes[OfferStatusCode.workCompleted].glow,
  },
];

// ════════════════════════════════════════════════════════════════
//  Props
// ════════════════════════════════════════════════════════════════

interface ActionRequiredStripProps {
  offersFilters?: OffersFilters;
  activeFilterCode?: number | null;
  translate: (key: string, options?: any) => any;
  onBadgeClick: (statusCode: OfferStatusCode) => void;
  onAllClick: () => void;
}

// ════════════════════════════════════════════════════════════════
//  Component
// ════════════════════════════════════════════════════════════════

const ActionRequiredStrip: React.FC<ActionRequiredStripProps> = ({
  offersFilters,
  activeFilterCode,
  translate,
  onBadgeClick,
  onAllClick,
}) => {
  const isPhone = useMediaQuery("(max-width:768px)");

  // Compute counts from offersFilters
  const badgeCounts = useMemo(() => {
    const counts: Record<number, number> = {};
    STRIP_BADGES.forEach((badge) => {
      const filter = offersFilters?.statusCodes?.find(
        (f) => f.id === badge.statusCode
      );
      counts[badge.statusCode] = filter?.value ?? 0;
    });
    return counts;
  }, [offersFilters]);

  // Total actionable items
  const totalActionable = useMemo(
    () => Object.values(badgeCounts).reduce((sum, c) => sum + c, 0),
    [badgeCounts]
  );

  // Hide strip when nothing needs action
  if (totalActionable === 0) return null;

  const isAllActive = activeFilterCode == null;

  return (
    <Box
      sx={{
        backgroundColor: dt.cardBg,
        borderRadius: dt.radiusSm,
        border: `1px solid ${dt.cardBorder}`,
        boxShadow: dt.cardShadow,
        padding: isPhone ? "10px 12px" : "12px 20px",
        display: "flex",
        alignItems: "center",
        gap: isPhone ? "8px" : "12px",
        overflowX: "auto",
        overflowY: "hidden",
        minHeight: isPhone ? "52px" : "56px",
        animation: "fadeUp 0.4s cubic-bezier(0.4, 0, 0.2, 1) both",
        // Hide scrollbar but still allow horizontal scroll on mobile
        "&::-webkit-scrollbar": { display: "none" },
        scrollbarWidth: "none",
      }}
    >
      {/* Strip label (desktop only) */}
      {!isPhone && (
        <Box
          sx={{
            fontSize: "11px",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "1.2px",
            color: dt.textMuted,
            fontFamily: dt.font,
            whiteSpace: "nowrap",
            flexShrink: 0,
            mr: "4px",
          }}
        >
          {translate("actionStrip.title")}
        </Box>
      )}

      {/* Divider (desktop only) */}
      {!isPhone && (
        <Box
          sx={{
            width: "1px",
            height: "28px",
            backgroundColor: dt.cardBorder,
            flexShrink: 0,
          }}
        />
      )}

      {/* "All" badge */}
      <Box
        onClick={onAllClick}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          backgroundColor: isAllActive ? "#F0F2F7" : "#F8F9FA",
          border: `1px solid ${isAllActive ? "#9CA3AF40" : "#E5E7EB"}`,
          borderRadius: dt.radiusPill,
          padding: isPhone ? "5px 10px" : "6px 14px",
          cursor: "pointer",
          whiteSpace: "nowrap",
          flexShrink: 0,
          transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
          ...(isAllActive && {
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
          }),
          "&:hover": {
            transform: "translateY(-1px)",
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            borderColor: "#9CA3AF40",
          },
        }}
      >
        {/* Count circle */}
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "20px",
            height: "20px",
            borderRadius: "10px",
            backgroundColor: isAllActive ? "#6B7280" : "#9CA3AF",
            color: "#FFFFFF",
            fontSize: "10px",
            fontWeight: 700,
            fontFamily: dt.fontMono,
            padding: "0 4px",
          }}
        >
          {totalActionable}
        </Box>

        {/* Icon */}
        <Box sx={{ display: "flex", color: isAllActive ? "#6B7280" : "#9CA3AF" }}>
          <AllIcon sx={{ fontSize: "15px" }} />
        </Box>

        {/* Label */}
        <Box
          sx={{
            fontSize: isPhone ? "11px" : "12px",
            fontWeight: 600,
            fontFamily: dt.font,
            color: isAllActive ? "#6B7280" : "#9CA3AF",
            letterSpacing: "0.1px",
          }}
        >
          {translate("actionStrip.all")}
        </Box>
      </Box>

      {/* Status badges */}
      {STRIP_BADGES.map((badge) => {
        const count = badgeCounts[badge.statusCode];
        if (count === 0) return null;

        const isActive = activeFilterCode === badge.statusCode;

        return (
          <Box
            key={badge.statusCode}
            onClick={() => onBadgeClick(badge.statusCode)}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              backgroundColor: badge.bg,
              border: `1px solid ${isActive ? badge.accent + "60" : badge.accent + "20"}`,
              borderRadius: dt.radiusPill,
              padding: isPhone ? "5px 10px" : "6px 14px",
              cursor: "pointer",
              whiteSpace: "nowrap",
              flexShrink: 0,
              transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
              ...(isActive && {
                boxShadow: `0 2px 8px ${badge.glow}, inset 0 0 0 1px ${badge.accent}30`,
              }),
              ...(badge.pulse && !isActive && {
                animation: "stripPulse 2.5s ease-in-out infinite",
              }),
              "&:hover": {
                transform: "translateY(-1px)",
                boxShadow: `0 2px 8px ${badge.glow}`,
                borderColor: `${badge.accent}40`,
              },
            }}
          >
            {/* Count circle */}
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "20px",
                height: "20px",
                borderRadius: "10px",
                backgroundColor: badge.accent,
                color: "#FFFFFF",
                fontSize: "10px",
                fontWeight: 700,
                fontFamily: dt.fontMono,
                padding: "0 4px",
              }}
            >
              {count}
            </Box>

            {/* Icon */}
            <Box sx={{ display: "flex", color: badge.accent }}>
              {badge.icon}
            </Box>

            {/* Label */}
            <Box
              sx={{
                fontSize: isPhone ? "11px" : "12px",
                fontWeight: 600,
                fontFamily: dt.font,
                color: badge.accent,
                letterSpacing: "0.1px",
              }}
            >
              {translate(badge.labelKey)}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default ActionRequiredStrip;
